<template>
  <v2-modal
    :show="show"
    title="Currencies"
    size="lg"
    @close="onClose"
  >
    <!-- Main currency list view -->
    <template v-if="!selectedCurrencyForImport">
      <div :class="{ 'v2-currency-books--disabled': isExportingCurrency }">
        <!-- Add new currency -->
        <div class="v2-currency-books__actions">
          <v2-button
            v-if="!showAddCurrencyField"
            variant="primary"
            size="sm"
            @click="showAddCurrencyField = true"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="12"
                y1="5"
                x2="12"
                y2="19"
              />
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              />
            </svg>
            Add new
          </v2-button>

          <div
            v-else
            class="v2-currency-books__add-form"
          >
            <div class="v2-currency-books__add-title">
              Add new currency
            </div>
            <v2-dropdown
              :value="selectedCurrency"
              :options="currencies"
              placeholder="Select currency"
              searchable
              empty-text="Nothing was found. Try another currency."
              class="v2-mt-2"
              @select="onSelectCurrency"
              @search="searchCurrency"
            />
            <div class="v2-currency-books__add-actions">
              <v2-button
                variant="outline"
                size="sm"
                :disabled="isSavingCurrency"
                @click="showAddCurrencyField = false"
              >
                Cancel
              </v2-button>
              <v2-button
                variant="primary"
                size="sm"
                :disabled="!selectedCurrency || isSavingCurrency"
                :loading="isSavingCurrency"
                @click="onAddCurrency"
              >
                Add
              </v2-button>
            </div>
          </div>
        </div>

        <!-- Master Currency Book -->
        <div class="v2-currency-books__section">
          <div class="v2-currency-books__section-title">
            Master Currency Book
          </div>
          <div class="v2-currency-books__row v2-currency-books__row--bordered">
            <div class="v2-currency-books__row-label">
              <span class="v2-currency-books__muted-label">Base Currency:</span>
              {{ baseCurrency }}
            </div>
            <div class="v2-currency-books__row-actions">
              <v2-button
                variant="outline"
                size="sm"
                :loading="isExportingCurrency && !exportingCurrency"
                @click="exportCurrencies()"
              >
                <svg
                  v-if="!(isExportingCurrency && !exportingCurrency)"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line
                    x1="12"
                    y1="3"
                    x2="12"
                    y2="15"
                  />
                </svg>
                Export
              </v2-button>
              <v2-button
                variant="outline"
                size="sm"
                @click="onClickImportCurrency(baseCurrency)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line
                    x1="12"
                    y1="15"
                    x2="12"
                    y2="3"
                  />
                </svg>
                Import
              </v2-button>
            </div>
          </div>
        </div>

        <!-- Additional Currencies -->
        <div
          v-if="currencyFileList.length"
          class="v2-currency-books__section"
        >
          <div class="v2-currency-books__section-title">
            Additional Currencies ({{ currencyFileList.length }})
          </div>
          <div
            v-for="currency of currencyFileList"
            :key="currency"
            class="v2-currency-books__row"
          >
            <div class="v2-currency-books__row-label">
              {{ currency }}
            </div>
            <div class="v2-currency-books__row-actions">
              <v2-button
                variant="outline"
                size="sm"
                :loading="isExportingCurrency && exportingCurrency === currency"
                @click="exportCurrencies(currency)"
              >
                <svg
                  v-if="!(isExportingCurrency && exportingCurrency === currency)"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line
                    x1="12"
                    y1="3"
                    x2="12"
                    y2="15"
                  />
                </svg>
                Export
              </v2-button>
              <v2-button
                variant="outline"
                size="sm"
                @click="onClickImportCurrency(currency)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line
                    x1="12"
                    y1="15"
                    x2="12"
                    y2="3"
                  />
                </svg>
                Import
              </v2-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Import file view -->
    <template v-else>
      <div
        :class="{ 'v2-currency-books--disabled': isUploadingCurrencyBook }"
        @dragover.prevent
        @drop.prevent
      >
        <p class="v2-currency-books__import-label">
          Upload file for {{ selectedCurrencyForImport === baseCurrency ? 'Master Currency Book' : selectedCurrencyForImport }}
        </p>

        <!-- File drop zone -->
        <div
          v-if="!currencyBookFile"
          class="v2-currency-books__dropzone"
          :class="{ 'v2-currency-books__dropzone--dragging': isDragging }"
          @click="$refs.fileInput.click()"
          @drop="onDropCurrencyFile"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--v2-primary)"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="16 16 12 12 8 16" />
            <line
              x1="12"
              y1="12"
              x2="12"
              y2="21"
            />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            <polyline points="16 16 12 12 8 16" />
          </svg>
          <span class="v2-currency-books__dropzone-title">Drag & Drop</span>
          <span class="v2-currency-books__dropzone-subtitle">or click to select files from your system</span>
        </div>

        <!-- File selected preview -->
        <div
          v-else
          class="v2-currency-books__file-preview"
        >
          <div class="v2-currency-books__file-info">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--v2-success)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div class="v2-currency-books__file-details">
              <span class="v2-currency-books__file-name">{{ currencyBookFile.name }}</span>
              <span class="v2-currency-books__file-size">{{ getSizeInKb(currencyBookFile.size) }} KB</span>
            </div>
          </div>
          <button
            class="v2-currency-books__file-remove"
            @click="currencyBookFile = null"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          hidden
          @change="onFileSelected"
        >
      </div>
    </template>

    <template #footer>
      <template v-if="selectedCurrencyForImport">
        <v2-button
          variant="outline"
          :disabled="isUploadingCurrencyBook"
          @click="resetCurrencyInput"
        >
          Back
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isUploadingCurrencyBook || !currencyBookFile"
          :loading="isUploadingCurrencyBook"
          @click="handleCurrencyImport"
        >
          {{ isUploadingCurrencyBook ? 'Uploading...' : 'Upload' }}
        </v2-button>
      </template>
    </template>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import { getSizeInKb } from '@/@core/utils/utils'
import { apiToastSuccess, apiToastSuccessV2, apiToastWarning } from '@/@core/utils/toast'
import { availableCurrencies, FILE_FORMATS } from '@/constants'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import {
  EXPORT_CURRENCIES,
  IMPORT_CURRENCIES,
  UPDATE_CURRENCIES,
  SET_IS_UPLOADING_CURRENCY_BOOK,
} from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS, DOWNLOAD_FILE } from '@/store/modules/common.module'
import {
  GET_ORDERS_INFO_BY_COLLECTION_ID_AND_CURRENCY,
} from '@/store/modules/order.module'

const { FILE_TYPES } = c

export default {
  name: 'V2CurrencyBooksModal',
  components: {
    V2Modal,
    V2Button,
    V2Dropdown,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collection: {
      type: Object,
      default: () => ({}),
    },
    fetchCollections: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      showAddCurrencyField: false,
      selectedCurrency: null,
      currencyFileList: [],
      currencies: availableCurrencies || [],
      isExportingCurrency: false,
      exportingCurrency: '',
      currencyBookFile: null,
      selectedCurrencyForImport: '',
      isSavingCurrency: false,
      conflictingOrders: [],
      isDragging: false,
      getSizeInKb,
    }
  },
  computed: {
    isUploadingCurrencyBook: {
      get() {
        return this.$store.state.collection.isUploadingCurrencyBook
      },
      set(value) {
        this.$store.commit(SET_IS_UPLOADING_CURRENCY_BOOK, value)
      },
    },
    baseCurrency() {
      return this.$store.getters.profile?.currency
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.currencyFileList = this.collection?.availableCurrencies || []
      }
    },
    isUploadingCurrencyBook(val) {
      if (val) {
        window.addEventListener('beforeunload', this.handleBeforeUnload)
      } else {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
      }
    },
  },
  methods: {
    handleBeforeUnload(e) {
      e.preventDefault()
      e.returnValue = ''
    },
    onClose() {
      if (!this.isUploadingCurrencyBook) {
        this.resetCurrencyInput()
        this.$emit('close')
      }
    },
    resetCurrencyInput() {
      this.selectedCurrency = null
      this.showAddCurrencyField = false
      this.selectedCurrencyForImport = ''
      this.currencyBookFile = null
    },
    searchCurrency(searchString) {
      const searchRegex = new RegExp(searchString, 'i')
      this.currencies = availableCurrencies.filter(currency => searchRegex.test(currency))
    },
    onSelectCurrency(currency) {
      if (this.selectedCurrency === currency) {
        this.selectedCurrency = null
        return
      }
      this.selectedCurrency = currency
    },
    onAddCurrency() {
      if (!this.selectedCurrency) return
      if (this.currencyFileList.includes(this.selectedCurrency) || this.selectedCurrency === this.baseCurrency) {
        apiToastWarning('Base currency prices can be updated from collection book')
        return
      }
      this.isSavingCurrency = true
      const payload = {
        currencies: [...this.currencyFileList, this.selectedCurrency],
      }
      this.$store.dispatch(UPDATE_CURRENCIES, { collectionId: this.collection._id, payload })
        .then(res => {
          apiToastSuccess(res.data.message)
          this.isSavingCurrency = false
          this.currencyFileList.push(this.selectedCurrency)
          this.resetCurrencyInput()
          this.fetchCollections()
        })
        .catch(() => {
          this.isSavingCurrency = false
        })
    },
    async exportCurrencies(currency = '') {
      this.isExportingCurrency = true
      this.exportingCurrency = currency

      const params = {
        collectionId: this.collection._id,
      }
      let fileName = ''
      if (currency) {
        params.currency = currency
        fileName = `${this.collection.name}-${currency}-currency-book.xlsx`
      } else {
        fileName = `${this.collection.name}-master-currency-book.xlsx`
      }

      const payload = {
        url: await this.$store.dispatch(EXPORT_CURRENCIES, params),
        fileName,
        fileFormat: FILE_FORMATS.XLSX,
        successMessage: 'Currency book exported successfully',
      }
      this.$store.dispatch(DOWNLOAD_FILE, payload)
        .then(() => {
          this.isExportingCurrency = false
          this.exportingCurrency = ''
        })
        .catch(() => {
          this.isExportingCurrency = false
          this.exportingCurrency = ''
        })
    },
    onClickImportCurrency(currency = '') {
      this.selectedCurrencyForImport = currency
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.currencyBookFile = file
      }
      event.target.value = ''
    },
    onDropCurrencyFile(event) {
      this.isDragging = false
      event.preventDefault()
      this.currencyBookFile = event.dataTransfer.files[0]
    },
    async getCollectionOrderData() {
      try {
        const response = await this.$store.dispatch(GET_ORDERS_INFO_BY_COLLECTION_ID_AND_CURRENCY, {
          collectionId: this.collection._id,
          currencies: this.selectedCurrencyForImport === this.baseCurrency
            ? this.currencyFileList
            : [this.selectedCurrencyForImport],
        })
        return response.data.data
      } catch (error) {
        console.error(error)
        return []
      }
    },
    async handleCurrencyImport() {
      this.isUploadingCurrencyBook = true
      try {
        const orderData = await this.getCollectionOrderData()
        if (orderData.length > 0) {
          this.conflictingOrders = orderData.map(order => ({ ...order, orderId: order._id }))
          this.isUploadingCurrencyBook = false
          this.$emit('show-currency-update-warning', {
            collectionId: this.collection._id,
            orderData: this.conflictingOrders,
          })
        } else {
          await this.importCurrencyBookFile([])
        }
      } catch (error) {
        this.isUploadingCurrencyBook = false
      }
    },
    async importCurrencyBookFile(selectedOrderIds) {
      this.isUploadingCurrencyBook = true
      try {
        const fileUrl = await this.$store.dispatch(UPLOAD_DOCUMENTS, {
          file: this.currencyBookFile,
          fileType: FILE_TYPES.MULTICURRENCY_BOOK,
        })
        const params = {
          collectionId: this.collection._id,
        }
        if (this.selectedCurrencyForImport && this.selectedCurrencyForImport !== this.baseCurrency) {
          params.currency = this.selectedCurrencyForImport
        }
        const importResponse = await this.$store.dispatch(IMPORT_CURRENCIES, {
          params,
          payload: { fileUrl, orderIds: selectedOrderIds },
        })
        apiToastSuccessV2(importResponse.data.message)
        this.currencyBookFile = null
        this.selectedCurrencyForImport = ''
      } catch (error) {
        // error toast handled by axios interceptor
      } finally {
        this.isUploadingCurrencyBook = false
        this.conflictingOrders = []
      }
    },
    handleDoNotUpdateOrders() {
      this.importCurrencyBookFile([])
    },
    handleUpdateOrders(orderIds) {
      this.importCurrencyBookFile(orderIds)
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-currency-books {
  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: var(--v2-space-4);
  }

  &__add-form {
    width: 100%;
    padding: var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__add-title {
    font-family: var(--v2-font-sans);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--v2-primary);
  }

  &__add-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-3);
  }

  &__section {
    margin-top: var(--v2-space-4);
  }

  &__section-title {
    font-family: var(--v2-font-sans);
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) 0;
    border-top: 1px solid var(--v2-border);

    &--bordered {
      border-bottom: 1px solid var(--v2-border);
    }
  }

  &__row-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
  }

  &__muted-label {
    color: var(--v2-muted-foreground);
    white-space: nowrap;
  }

  &__row-actions {
    display: flex;
    gap: var(--v2-space-2);
  }

  &__import-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-3);
  }

  &__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-8) var(--v2-space-4);
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-lg);
    cursor: pointer;
    transition: all 0.2s ease;
    background: var(--v2-background);

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &--dragging {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }
  }

  &__dropzone-title {
    font-family: var(--v2-font-sans);
    font-weight: 700;
    font-size: 1rem;
    color: var(--v2-foreground);
    margin-top: var(--v2-space-2);
  }

  &__dropzone-subtitle {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-1);
  }

  &__file-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-3) var(--v2-space-4);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-muted);
  }

  &__file-info {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
  }

  &__file-details {
    display: flex;
    flex-direction: column;
  }

  &__file-name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
  }

  &__file-size {
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__file-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: var(--v2-destructive);
    border: none;
    border-radius: var(--v2-radius-sm);
    color: #fff;
    cursor: pointer;
    transition: filter 0.15s ease;

    &:hover {
      filter: brightness(1.1);
    }
  }
}

.v2-mt-2 {
  margin-top: var(--v2-space-2);
}
</style>
